<script setup lang="ts">
import type { IUserInfoExtended } from '~/interfaces/dto/auth/response';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';

const props = withDefaults(defineProps<Partial<IUserInfoExtended>>(), {
  email: '',
  username: '',
});

const { $t, $trackEvent } = useNuxtApp();
const { $authService } = useServices();
const auth = useAuthStore();
const site = useSiteStore();

const emits = defineEmits<{ (e: 'update'): void; (e: 'empty-schema'): void }>();

const form = ref<IFormBuilderReference>();
const errorCode = ref<string>('');
const errorRef = ref<string>('');
const userDetails = ref<Partial<IUserInfoExtended>>({
  email: props.email.includes('mobile.no') ? '' : props.email,
  username: props.username,
});

const disableUpdate = computed(() => {
  if (!!form.value)
    return !(
      form?.value?.validated &&
      !form?.value?.invalid &&
      userDetails.value.email.toLowerCase() !== props.email.toLowerCase()
    );
  else return true;
});

const updateEmailHandler = async () => {
  if (disableUpdate.value) return;
  try {
    errorCode.value = '';
    errorRef.value = '';
    const requestBody = {
      ...userDetails.value,
      email: userDetails.value.email.toLowerCase().trim(),
      username: auth.user.username,
    };
    const data = await $authService.editUser(requestBody);
    if (data?.isSuccessful) {
      site.toggleSiteNotice({
        severity: 'success',
        heading: $t('success'),
        content: {
          data: $t(`pers-details-updated-succ`),
        },
        timeout: 3000,
        enabled: true,
      });
      emits('update');
      await $trackEvent('Account', 'MY_ACCOUNT_UPDATE_EMAIL');
    } else {
      errorCode.value = data?.error?.displayCode + '';
      errorRef.value = data?.error?.code + '';
      form.value.formErrors['email'] = `update-details-${data?.error?.code}`;
    }
  } catch (e) {
    errorCode.value = e?.error?.displayCode || e?.error?.code;
    errorRef.value = '';
    form.value.formErrors['email'] =
      `update-details-${e?.error?.displayCode || e?.error?.code}`;
  }
};

onMounted(() => {
  userDetails.value.email = props.email.includes('mobile.no')
    ? ''
    : props.email;
  userDetails.value.username = props.username;
});
</script>

<template>
  <div
    v-show="!!form && form.loaded"
    class="px-4 pb-4 border-t border-light-50 dark:border-dark-900"
  >
    <p class="pb-3 text-base font-bold mt-4">
      {{ $t('account-email-address') }}
    </p>
    <UserInterfaceDynamicFormBuilder
      ref="form"
      v-model="userDetails"
      template-name="update email"
      :should-auto-focus="false"
      @empty-schema="emits('empty-schema')"
      @error="errorCode = $event"
      @keyup.enter="updateEmailHandler"
      @edited="((errorCode = ''), (errorRef = ''))"
    >
      <template v-if="form" #form-actions="formActionProps">
        <UserInterfaceGenericError
          v-if="errorCode"
          state="danger"
          class="max-w-72 mx-auto mb-2"
          :reference-code="errorRef"
        >
          {{ $t(`update-details-${errorCode}`) }}
        </UserInterfaceGenericError>
        <DesignSystemButton
          class="w-full justify-center mt-3"
          rounding="md"
          :disabled="
            !(
              formActionProps?.form?.validated &&
              !formActionProps?.form?.invalid
            ) || userDetails.email.toLowerCase() === props.email.toLowerCase()
          "
          @click="updateEmailHandler"
        >
          {{ $t('update') }}
        </DesignSystemButton>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
</template>
